export interface MindsetWebRuntimeConfig {
  firebase: {
    apiKey: string;
    appId: string;
    authDomain: string;
    projectId: string;
  };
  gatewayUrl: string;
  realtimeUrl?: string;
  tenantId: string;
}

declare global {
  interface Window {
    __MINDSET_WEB_CONFIG__?: Partial<{
      firebaseApiKey: string;
      firebaseAppId: string;
      firebaseAuthDomain: string;
      firebaseProjectId: string;
      gatewayUrl: string;
      realtimeUrl: string;
      tenantId: string;
      widgetTurnstileSiteKey: string;
    }>;
  }
}

export class MindsetWebConfigurationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MindsetWebConfigurationError";
  }
}

export function readMindsetWebRuntimeConfig(): MindsetWebRuntimeConfig {
  const runtime = typeof window === "undefined" ? undefined : window.__MINDSET_WEB_CONFIG__;
  const gatewayUrl = first(runtime?.gatewayUrl, import.meta.env.VITE_MINDSET_MOBILE_GATEWAY_URL);
  const realtimeUrl = first(runtime?.realtimeUrl, import.meta.env.VITE_MINDSET_REALTIME_URL);
  if (realtimeUrl && !validRealtimeUrl(realtimeUrl)) {
    throw new MindsetWebConfigurationError("Mindset realtime configuration is invalid.");
  }
  const tenantId = first(runtime?.tenantId, import.meta.env.VITE_MINDSET_TENANT_ID) || "mindset-lab";
  const firebaseApiKey = first(runtime?.firebaseApiKey, import.meta.env.VITE_MINDSET_FIREBASE_API_KEY);
  const firebaseAppId = first(runtime?.firebaseAppId, import.meta.env.VITE_MINDSET_FIREBASE_APP_ID);
  const firebaseAuthDomain = first(runtime?.firebaseAuthDomain, import.meta.env.VITE_MINDSET_FIREBASE_AUTH_DOMAIN);
  const firebaseProjectId = first(runtime?.firebaseProjectId, import.meta.env.VITE_MINDSET_FIREBASE_PROJECT_ID);

  if (!validGatewayUrl(gatewayUrl)) {
    throw new MindsetWebConfigurationError("Mindset gateway is not configured for this site.");
  }
  if (!/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/u.test(tenantId)) {
    throw new MindsetWebConfigurationError("Mindset tenant configuration is invalid.");
  }
  if (!/^[A-Za-z0-9_-]{20,128}$/u.test(firebaseApiKey)) {
    throw new MindsetWebConfigurationError("Firebase web access is not configured for this site.");
  }
  if (!/^1:[0-9]+:web:[A-Za-z0-9]+$/u.test(firebaseAppId)) {
    throw new MindsetWebConfigurationError("Firebase web access is not configured for this site.");
  }
  if (!validHostname(firebaseAuthDomain)) {
    throw new MindsetWebConfigurationError("Firebase web access is not configured for this site.");
  }
  if (!/^[a-z][a-z0-9-]{4,61}[a-z0-9]$/u.test(firebaseProjectId)) {
    throw new MindsetWebConfigurationError("Firebase web access is not configured for this site.");
  }

  return {
    firebase: {
      apiKey: firebaseApiKey,
      appId: firebaseAppId,
      authDomain: firebaseAuthDomain,
      projectId: firebaseProjectId,
    },
    gatewayUrl: gatewayUrl.replace(/\/+$/u, ""),
    ...(realtimeUrl ? { realtimeUrl } : {}),
    tenantId,
  };
}

function first(...values: Array<string | undefined>): string {
  return values.find((value) => value?.trim())?.trim() ?? "";
}

function validGatewayUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return (
      url.username === "" &&
      url.password === "" &&
      url.pathname === "/" &&
      url.search === "" &&
      url.hash === "" &&
      (url.protocol === "https:" ||
        (url.protocol === "http:" && ["localhost", "127.0.0.1"].includes(url.hostname)))
    );
  } catch {
    return false;
  }
}

function validHostname(value: string): boolean {
  if (value.length > 253 || value.includes("/")) return false;
  return value.split(".").every((label) =>
    /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/u.test(label),
  );
}

function validRealtimeUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return !url.username && !url.password && !url.search && !url.hash &&
      url.pathname === "/mindset/v1/realtime" &&
      (url.protocol === "wss:" || (url.protocol === "ws:" && ["localhost", "127.0.0.1"].includes(url.hostname)));
  } catch { return false; }
}
